<template>
  <div class="ccma-company-name">
    <!-- Full-screen loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('consultantCorner.companyName.LoadingCompanyName') }}</p>
      </div>
    </div>

    <div v-if="showError" class="ccma-company-name-error">
      <h2>{{ t('consultantCorner.companyName.PageLoadError') }}</h2>
    </div>

    <!-- Error notification for save operations -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.companyName.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="saveCompanyName">{{ t('consultantCorner.companyName.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.companyName.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <!-- Opt-out success notification -->
    <div v-if="optOutSaveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.companyName.OptOutUpdateSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearOptOutSuccess">×</button>
      </div>
    </div>

    <!-- Opt-out error notification -->
    <div v-if="optOutSaveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.companyName.OptOutUpdateError') }}</h3>
          <p>{{ t('consultantCorner.companyName.OptOutUpdateErrorMessage') }}</p>
        </div>
        <button class="error-close" @click="clearOptOutError">×</button>
      </div>
    </div>

    <div v-else-if="!showError && !isLoading">
      <!-- Header Section -->
      <div class="company-header">
        <div class="first">
          <div class="company-title">{{ t('consultantCorner.companyName.CompanyName') }}</div>
          <div class="company-subtitle mb-1">{{ t('consultantCorner.companyName.UpdateCompanyInfo') }}</div>
        </div>
        <div class="company-buttons">
          <button
            id="btnSave"
            class="baseBtn primaryBtn"
            type="button"
            @click="saveCompanyName"
            :disabled="isSaving || !isFormValid"
          >
            {{ isSaving ? t('consultantCorner.companyName.Saving') : t('consultantCorner.companyName.SaveCompanyName') }}
          </button>
        </div>
      </div>

      <!-- Company Name Form -->
      <div class="company-form-container">
        <div class="company-form-content">
          <form @submit.prevent="saveCompanyName">
            <fieldset class="k-form-fieldset">

              <!-- Business Name Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.companyName.BusinessNameSection') }}</h3>
                <div class="form-row">
                  <div class="form-field full-width">
                    <label class="form-label required">{{ t('consultantCorner.companyName.BusinessName') }}</label>
                    <input
                      v-model="businessName"
                      type="text"
                      class="form-input"
                      :class="{ 'error': errors.businessName }"
                      :placeholder="t('consultantCorner.companyName.BusinessNamePlaceholder')"
                      required
                      @input="updateConsultantUrl"
                      data-testid="businessName"
                    />
                    <div v-if="errors.businessName" class="error-message">{{ errors.businessName }}</div>
                    <div class="help-text">{{ t('consultantCorner.companyName.BusinessNameHelp') }}</div>
                  </div>
                </div>
              </div>

              <!-- Consultant URL Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.companyName.ConsultantUrlSection') }}</h3>
                <div class="form-row">
                  <div class="form-field full-width">
                    <label class="form-label">{{ t('consultantCorner.companyName.ConsultantUrl') }}</label>
                    <!-- spans keept on one line in order to prevent a space from rendering between them in the browser -->
                    <div class="url-display"><span class="url-base">{{ pwsBaseUrl }}</span><span class="url-business-name">{{ businessNameForUrl || t('consultantCorner.companyName.UrlPlaceholder') }}</span></div>
                    <div class="help-text">{{ t('consultantCorner.companyName.UrlHelp') }}</div>
                  </div>
                </div>
              </div>

              <!-- Search Visibility Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.companyName.SearchVisibilitySection') }}</h3>
                <div class="form-row">
                  <div class="form-field full-width">
                    <div class="checkbox-container">
                      <label class="checkbox-wrapper">
                        <input
                          v-model="optOutFromSearch"
                          type="checkbox"
                          class="checkbox-input"
                        />
                        <span class="checkbox-custom"></span>
                        <span class="checkbox-text">
                          {{ t('consultantCorner.companyName.OptOutFromSearch') }}
                        </span>
                      </label>
                    </div>
                    <div class="help-text">{{ t('consultantCorner.companyName.OptOutHelp') }}</div>
                    <div class="opt-out-actions">
                      <button
                        type="button"
                        class="baseBtn primaryBtn"
                        @click="saveOptOutSettings"
                        :disabled="isOptOutSaving"
                      >
                        {{ isOptOutSaving ? t('consultantCorner.companyName.Updating') : t('consultantCorner.companyName.UpdateOptOut') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { PwsProfileBusinessNameGet, PwsBeeConsultantSearchOptoutGet, PwsBeeConsultantSearchOptoutSave, PwsBeeConsultantSearchOptoutUpdate } from '../../api/ConsultantCorner'
import { BusinessNameUpdate } from '../../api/Business'
import { useStoreLogger } from '../../logger'
import { GetCountryCode, GetBeeNumberAuthenicatedUser, GetPwsBaseUrl } from '../../stores/SettingsStore'
import { Loader } from '@progress/kendo-vue-indicators'
import { getCleanString } from '../../utils/businessNameHelper'

const { t } = useI18n()
const logger = useStoreLogger()

// Reactive state
const showError = ref(false)
const isSaving = ref(false)
const saveError = ref(false)
const saveErrorMessage = ref('')
const saveSuccess = ref(false)
const countryCode = ref('')
const pwsBaseUrl = ref('')
const isLoading = ref(false)
const businessName = ref('')
const optOutFromSearch = ref(false)
const isOptOutSaving = ref(false)
const optOutSaveError = ref(false)
const optOutSaveSuccess = ref(false)

const errors = reactive({
  businessName: ''
})

// Computed properties
const businessNameForUrl = computed(() => getCleanString(businessName.value))

const isFormValid = computed(() => {
  return businessName.value.trim() !== '' && !errors.businessName
})

// Methods
const validateForm = (): boolean => {
  // Clear previous errors
  errors.businessName = ''

  let isValid = true

  // Validate business name
  if (!businessName.value.trim()) {
    errors.businessName = t('consultantCorner.companyName.BusinessNameRequired')
    isValid = false
  } else if (businessName.value.trim().length < 2) {
    errors.businessName = t('consultantCorner.companyName.BusinessNameTooShort')
    isValid = false
  } else if (businessName.value.trim().length > 100) {
    errors.businessName = t('consultantCorner.companyName.BusinessNameTooLong')
    isValid = false
  } else if (/[^\x00-\x7F]/.test(businessName.value.trim())) {
    errors.businessName = t('consultantCorner.companyName.BusinessNameInvalidCharacters')
    isValid = false
  }

  return isValid
}

const updateConsultantUrl = () => {
  // This will automatically update the computed businessNameForUrl
  validateForm()
}

const loadCompanyData = async () => {
  try {
    isLoading.value = true
    showError.value = false

    // Load business name
    const businessNameResponse = await PwsProfileBusinessNameGet() as any

    if (businessNameResponse && businessNameResponse.businessName) {
      businessName.value = businessNameResponse.businessName
    }

    // Load opt-out settings
    const optOutResponse = await PwsBeeConsultantSearchOptoutGet() as any

    if (optOutResponse && optOutResponse.item) {
      optOutFromSearch.value = Boolean(optOutResponse.item.optOut)
    }

    logger.info('Company data loaded successfully', { businessNameResponse, optOutResponse })
  } catch (error) {
    logger.error('Error loading company data:', error)
    showError.value = true
  } finally {
    isLoading.value = false
  }
}

const saveCompanyName = async () => {
  if (!validateForm()) {
    saveErrorMessage.value = t('consultantCorner.companyName.ValidationError')
    saveError.value = true
    return
  }

  try {
    isSaving.value = true
    clearNotifications()

    // Get the authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (!beeNumber) {
      saveErrorMessage.value = t('consultantCorner.companyName.SaveErrorGeneric')
      saveError.value = true
      return
    }

    // Call the API to update the business name
    const response = await BusinessNameUpdate(beeNumber, businessNameForUrl.value)

    // Check for specific error responses
    if (response && response.responseStatus) {
      const responseStatus = response.responseStatus

      if (responseStatus.errorCode === 'BusinessNameAlreadyExists') {
        saveErrorMessage.value = t('consultantCorner.companyName.BusinessNameAlreadyExists')
        saveError.value = true
        return
      }

      if (responseStatus.errorCode || responseStatus.message) {
        const errorMessage = responseStatus.message || t('consultantCorner.companyName.SaveErrorGeneric')
        logger.error('ServiceStack error saving business name:', responseStatus)
        saveErrorMessage.value = getUserFriendlyErrorMessage(responseStatus)
        saveError.value = true
        return
      }
    }

    logger.info('Business name saved successfully')

    // Show success notification
    saveSuccess.value = true

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      clearNotifications()
    }, 3000)

  } catch (error: any) {
    logger.error('Error saving business name:', error)

    // Handle different types of errors
    if (error.response) {
      const status = error.response.status
      const data = error.response.data

      if (data && data.ResponseStatus) {
        saveErrorMessage.value = getUserFriendlyErrorMessage(data.ResponseStatus)
      } else if (status >= 500) {
        saveErrorMessage.value = t('consultantCorner.companyName.SaveErrorGeneric')
      } else if (status === 400) {
        saveErrorMessage.value = t('consultantCorner.companyName.ValidationError')
      } else {
        saveErrorMessage.value = `${t('consultantCorner.companyName.SaveError')}: ${status}`
      }
    } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      saveErrorMessage.value = t('consultantCorner.companyName.NetworkError')
    } else {
      saveErrorMessage.value = error.message || t('consultantCorner.companyName.SaveErrorGeneric')
    }

    saveError.value = true
  } finally {
    isSaving.value = false
  }
}

const getUserFriendlyErrorMessage = (responseStatus: any): string => {
  const errorCode = responseStatus.ErrorCode || responseStatus.errorCode
  const message = responseStatus.Message || responseStatus.message

  switch (errorCode) {
    case 'BusinessNameAlreadyExists':
      return t('consultantCorner.companyName.BusinessNameAlreadyExists')
    case 'ValidationException':
    case 'ArgumentException':
      return t('consultantCorner.companyName.ValidationError')
    case 'UnauthorizedAccessException':
    case 'AuthenticationException':
      return t('consultantCorner.companyName.UnauthorizedError')
    case 'TimeoutException':
      return t('consultantCorner.companyName.TimeoutError')
    default:
      return message || t('consultantCorner.companyName.SaveErrorGeneric')
  }
}

const clearNotifications = () => {
  saveError.value = false
  saveSuccess.value = false
  saveErrorMessage.value = ''
}

const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}

const clearOptOutSuccess = () => {
  optOutSaveSuccess.value = false
}

const clearOptOutError = () => {
  optOutSaveError.value = false
}

const saveOptOutSettings = async () => {
  try {
    isOptOutSaving.value = true
    optOutSaveError.value = false
    optOutSaveSuccess.value = false

    const request = {
      optOut: optOutFromSearch.value
    }

    // Try save first, then update if needed
    try {
      await PwsBeeConsultantSearchOptoutSave(request)
    } catch (saveError) {
      // If save fails, try update
      await PwsBeeConsultantSearchOptoutUpdate(request)
    }

    logger.info('Opt-out settings saved successfully', request)

    // Show success notification
    optOutSaveSuccess.value = true

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      optOutSaveSuccess.value = false
    }, 3000)

  } catch (error: any) {
    logger.error('Error saving opt-out settings:', error)
    optOutSaveError.value = true

    // Auto-hide error message after 5 seconds
    setTimeout(() => {
      optOutSaveError.value = false
    }, 5000)
  } finally {
    isOptOutSaving.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Load country code
  try {
    countryCode.value = await GetCountryCode()
    logger.info('Country code loaded:', countryCode.value)
  } catch (error) {
    logger.error('Error loading country code:', error)
    countryCode.value = '' // Default fallback
  }

  // Load PWS base URL
  try {
    pwsBaseUrl.value = await GetPwsBaseUrl()
  } catch (error) {
    logger.error('Error loading PWS base URL:', error)
    pwsBaseUrl.value = `https://www${(countryCode.value || '').toLowerCase() || 'us'}.tpc.thatchertech.io/pws/`
  }

  // Load business name data
  loadCompanyData()
})
</script>

<style lang="scss" scoped>
.ccma-company-name {
  padding: 1.5rem;
  width: 100%;
  position: relative;
}

.ccma-company-name-error {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-text {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #666;
}

/* Error notification styles */
.save-error-notification {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.error-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  gap: 0.75rem;
}

.error-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.error-details {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
  color: #721c24;
}

.error-details p {
  @include typography-body;
  margin: 0;
  color: #721c24;
}

.error-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.error-close:hover {
  background-color: rgba(#721c24, 0.1);
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #f5c6cb;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

/* Success notification styles */
.save-success-notification {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.success-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.75rem;
}

.success-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.success-message {
  flex: 1;
}

.success-message p {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #155724;
}

.success-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #155724;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.success-close:hover {
  background-color: rgba(#155724, 0.1);
}

/* Animation for notifications */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.company-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.company-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.25rem;
}

.company-subtitle {
  @include typography-body;
  color: #6c757d;
}

.company-buttons {
  display: flex;
  gap: 0.75rem;
}

.company-form-container {
  background: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  width: 100%;
}

.company-form-content {
  padding: 2rem;
  width: 100%;
  max-width: none;
}

.form-section {
  margin-bottom: 2rem;
}

.section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.form-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field.full-width {
  flex: 1 1 100%;
  width: 100%;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-input:focus {
  outline: none;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 0 0 0.2rem rgba(var(--vt-c-teal-dark), 0.3);
}

.form-input.error {
  border-color: #dc3545;
}

.form-input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

.help-text {
  @include typography-small;
  color: #6c757d;
  margin-top: 0.25rem;
}

/* URL Display Styles */
.url-display {
  padding: 0.75rem;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  border-radius: $radius-sm;
  @include typography-small;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  word-break: break-all;
}

.url-base {
  color: #6c757d;
}

.url-business-name {
  @include font-weight-medium;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

/* Checkbox Styles */
.checkbox-container {
  margin-bottom: 0.75rem;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  @include typography-body;
  @include font-weight-medium;
  gap: 0.75rem;
  cursor: pointer;
  color: #333;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkbox-custom {
  position: relative;
  width: 20px;
  height: 20px;
  border: 2px solid #ced4da;
  border-radius: $radius-sm;
  background-color: var(--vt-c-white);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-input:checked + .checkbox-custom {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.checkbox-input:checked + .checkbox-custom::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-wrapper:hover .checkbox-custom {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.checkbox-input:focus + .checkbox-custom {
  box-shadow: 0 0 0 0.2rem rgba(var(--vt-c-teal-dark), 0.3);
}

.checkbox-text {
  user-select: none;
}

.opt-out-actions {
  margin-top: 1rem;
}

.baseBtn {
  padding: 0.5rem 1.5rem;
  @include typography-body;
  @include font-weight-medium;
  border: none;
  border-radius: $button-radius;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.baseBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .ccma-company-name {
    padding: 1rem;
  }

  .company-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .company-buttons {
    margin-top: 0;
    justify-content: stretch;
    flex-direction: column;
    gap: 0.5rem;
  }

  .company-buttons .baseBtn {
    width: 100%;
  }

  .form-row {
    flex-direction: column;
    gap: 1rem;
  }

  .company-form-content {
    padding: 1.5rem 1rem;
  }

  .form-field {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .ccma-company-name {
    padding: 0.5rem;
  }

  .company-form-content {
    padding: 1rem;
  }

  .form-row {
    gap: 0.75rem;
  }
}
</style>
